/**
 * Gift items (D2, 0.9.40, `Funds\Modules\ItemsModule`, ADR 0082). The fund lists things a gift pays for — a food pack, a set
 * of books — each at its price; the donor picks a quantity of each and may add something extra. One-time gifts only.
 *
 * This collects the quantities and the extra and shows the prices as the fund lists them; it never adds up a total. Each item's
 * subtotal shown is the price times the quantity, as the design draws it; the gift's total, and what is charged, is always the
 * server's (its quote, then the payment), priced from the fund's own list.
 */

import { digitsOnly } from './digits.js';

const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

export class ItemsUi {
	/**
	 * @param {Object} wizard The checkout wizard (its formatting, `parseMinorUnits()`, `customAmount` and `updateSummary()`).
	 */
	constructor( wizard ) {
		this.wizard = wizard;
		this.items = Array.isArray( wizard.form.items ) ? wizard.form.items : [];
		this.quantities = {};
		this.rows = {};
		this.message = null;
	}

	/**
	 * The most of one item a gift can carry (`ItemsModule::MAX_QUANTITY`).
	 *
	 * @return {number}
	 */
	static get maxQuantity() {
		return 1000;
	}

	/**
	 * The section: "One-time gift. Pick as many as you like.", a row per item with its price, a minus and a plus and its subtotal,
	 * then "Add something extra (optional)".
	 *
	 * @return {HTMLElement}
	 */
	build() {
		const section = document.createElement( 'div' );
		const lead = document.createElement( 'p' );
		const list = document.createElement( 'ul' );

		section.className = 'anudan-checkout__items';
		section.setAttribute( 'data-anudan-field', 'items' );
		lead.className = 'anudan-checkout__field-help';
		lead.textContent = __( 'One-time gift. Pick as many as you like.' );
		list.className = 'anudan-checkout__items-list';

		this.items.forEach( ( item ) => list.appendChild( this.buildRow( item ) ) );

		section.appendChild( lead );
		section.appendChild( list );

		this.message = document.createElement( 'p' );
		this.message.className = 'anudan-checkout__field-error';
		this.message.setAttribute( 'role', 'alert' );
		this.message.hidden = true;
		section.appendChild( this.message );

		section.appendChild( this.buildExtra() );

		return section;
	}

	/**
	 * One item: "Food pack, $25.00", its subtotal, and its quantity between a minus and a plus.
	 *
	 * @param {Object} item `{ key, name, price }`, price in minor units.
	 * @return {HTMLElement}
	 */
	buildRow( item ) {
		const row = document.createElement( 'li' );
		const text = document.createElement( 'div' );
		const name = document.createElement( 'p' );
		const subtotal = document.createElement( 'p' );
		const figure = document.createElement( 'strong' );
		const id = `${ this.wizard.idPrefix }-item-${ item.key }`;

		row.className = 'anudan-checkout__item';
		text.className = 'anudan-checkout__item-text';
		name.className = 'anudan-checkout__item-name';
		/* translators: 1: the item's name, 2: its price, e.g. "$25.00". */
		name.textContent = __( '%1$s, %2$s' ).replace( '%1$s', item.name ).replace( '%2$s', this.wizard.formatMinorUnits( Number( item.price ) || 0 ) );
		subtotal.className = 'anudan-checkout__item-subtotal';
		subtotal.appendChild( document.createTextNode( `${ __( 'Subtotal' ) } ` ) );
		subtotal.appendChild( figure );
		text.appendChild( name );
		text.appendChild( subtotal );

		const input = document.createElement( 'input' );

		input.type = 'number';
		input.inputMode = 'numeric';
		input.id = id;
		input.className = 'anudan-checkout__field-input anudan-checkout__entries-input';
		input.min = '0';
		input.max = String( ItemsUi.maxQuantity );
		input.step = '1';
		input.value = '0';
		// R3-3 (rc4): digits only as it is typed or pasted.
		digitsOnly( input );
		/* translators: %s: the item's name. */
		input.setAttribute( 'aria-label', __( 'Quantity, %s' ).replace( '%s', item.name ) );
		input.addEventListener( 'input', () => this.setQuantity( item, input.value ) );

		const step = ( by, symbol, words ) => {
			const button = document.createElement( 'button' );

			button.type = 'button';
			button.className = 'anudan-checkout__step-button';
			button.textContent = symbol;
			button.setAttribute( 'aria-label', words.replace( '%s', item.name ) );
			button.addEventListener( 'click', () => this.setQuantity( item, ( this.quantities[ item.key ] || 0 ) + by ) );

			return button;
		};

		const stepper = document.createElement( 'div' );

		stepper.className = 'anudan-checkout__stepper';
		/* translators: %s: the item's name. */
		stepper.appendChild( step( -1, '−', __( 'Fewer, %s' ) ) );
		stepper.appendChild( input );
		/* translators: %s: the item's name. */
		stepper.appendChild( step( 1, '+', __( 'More, %s' ) ) );

		row.appendChild( text );
		row.appendChild( stepper );
		this.rows[ item.key ] = { input, figure, row };
		this.showSubtotal( item );

		return row;
	}

	/**
	 * The amount box, in the fund's currency. With no item chosen it reads "Or give any amount to this fund" and what is typed is
	 * the gift, a plain gift to the fund (decided after Phase 3b); once an item is chosen it reads "Add something extra (optional)"
	 * and what is typed is added to the items. Either way the server prices the gift.
	 *
	 * @return {HTMLElement}
	 */
	buildExtra() {
		const wrapper = document.createElement( 'div' );
		const label = document.createElement( 'label' );
		const id = `${ this.wizard.idPrefix }-items-extra`;

		wrapper.className = 'anudan-checkout__field anudan-checkout__items-extra';
		wrapper.setAttribute( 'data-anudan-field', 'amount' );
		label.className = 'anudan-checkout__field-label';
		label.htmlFor = id;
		this.extraLabel = label;
		this.showExtraLabel();

		const input = document.createElement( 'input' );

		input.type = 'text';
		input.inputMode = 'decimal';
		input.id = id;
		input.className = 'anudan-checkout__field-input';
		input.addEventListener( 'input', () => {
			const minor = this.wizard.parseMinorUnits( input.value );

			this.wizard.customAmount = null === minor || minor <= 0 ? null : minor;
			this.wizard.updateSummary();
		} );

		wrapper.appendChild( label );
		wrapper.appendChild( input );
		this.extraInput = input;

		return wrapper;
	}

	/**
	 * Words the amount box by whether an item is chosen: the gift on its own, or an extra on top of the items.
	 *
	 * @return {void}
	 */
	showExtraLabel() {
		if ( ! this.extraLabel ) {
			return;
		}

		if ( this.count() > 0 ) {
			this.extraLabel.textContent = __( 'Add something extra' );
			this.extraLabel.setAttribute( 'data-anudan-need', __( '(optional)' ) );
		} else {
			this.extraLabel.textContent = __( 'Or give any amount to this fund' );
			this.extraLabel.removeAttribute( 'data-anudan-need' );
		}
	}

	/**
	 * Sets an item's quantity: a whole number from 0 to the most allowed. Never a price.
	 *
	 * @param {Object}        item  The item.
	 * @param {string|number} value What was typed or stepped to.
	 * @return {void}
	 */
	setQuantity( item, value ) {
		const typed = parseInt( value, 10 );
		const quantity = Number.isFinite( typed ) ? Math.min( ItemsUi.maxQuantity, Math.max( 0, typed ) ) : 0;
		const row = this.rows[ item.key ];

		this.quantities[ item.key ] = quantity;

		if ( row && String( quantity ) !== row.input.value && '' !== row.input.value ) {
			row.input.value = String( quantity );
		}

		this.showSubtotal( item );
		this.showExtraLabel();
		this.showError( '' );
		this.wizard.updateSummary();
	}

	/**
	 * An item's subtotal, as the design shows it beside the item: its price times its quantity.
	 *
	 * @param {Object} item The item.
	 * @return {void}
	 */
	showSubtotal( item ) {
		const row = this.rows[ item.key ];

		if ( row ) {
			row.figure.textContent = this.wizard.formatMinorUnits( ( this.quantities[ item.key ] || 0 ) * ( Number( item.price ) || 0 ) );
			row.row.classList.toggle( 'is-chosen', ( this.quantities[ item.key ] || 0 ) > 0 );
		}
	}

	/**
	 * How many items are chosen, in all.
	 *
	 * @return {number}
	 */
	count() {
		return Object.values( this.quantities ).reduce( ( sum, quantity ) => sum + quantity, 0 );
	}

	/**
	 * What the server prices the gift from: each chosen item's key and quantity. Never a price or a total.
	 *
	 * @return {Object<string, number>}
	 */
	payload() {
		const out = {};

		this.items.forEach( ( item ) => {
			if ( ( this.quantities[ item.key ] || 0 ) > 0 ) {
				out[ item.key ] = this.quantities[ item.key ];
			}
		} );

		return out;
	}

	/**
	 * The "Your gift" card's lines, "2 × Food pack" with its subtotal, in the fund's order. Only shown: the total is the server's.
	 *
	 * @return {Array<Array>} [ words, minor units ] for each chosen item.
	 */
	summaryLines() {
		return this.items
			.filter( ( item ) => ( this.quantities[ item.key ] || 0 ) > 0 )
			.map( ( item ) => [
				/* translators: 1: how many, 2: the item's name. */
				__( '%1$s × %2$s' ).replace( '%1$s', String( this.quantities[ item.key ] ) ).replace( '%2$s', item.name ),
				this.quantities[ item.key ] * ( Number( item.price ) || 0 ),
			] );
	}

	/**
	 * Says what is wrong with the items, under them, or clears it.
	 *
	 * @param {string} text The message, or ''.
	 * @return {void}
	 */
	showError( text ) {
		if ( this.message ) {
			this.message.textContent = text;
			this.message.hidden = '' === text;
		}
	}

	/**
	 * What Continue says when nothing is chosen.
	 *
	 * @return {string}
	 */
	prompt() {
		return __( 'Please choose at least one item, or enter an amount.' );
	}
}
